import { ClinicalInput, EngineResult, evaluateClinicalState } from './engine'
import { Episode, EpisodeObservation, evaluateEpisode } from './episode'

export interface ObservationCorrectionRecord {
  id: string
  correctedAt: string
  reason?: string
  previousCollectedAt: string
  previousInput: ClinicalInput
  previousResult: EngineResult
}

export type EpisodeWithCorrections = Episode & {
  observationCorrections?: Record<string, ObservationCorrectionRecord[]>
}

export interface ObservationCorrectionInput {
  collectedAt: string
  input: ClinicalInput
  reason?: string
}

export function getObservationCorrections(episode: Episode, observationId: string): ObservationCorrectionRecord[] {
  return (episode as EpisodeWithCorrections).observationCorrections?.[observationId] ?? []
}

export function correctEpisodeObservation(
  episode: Episode,
  observationId: string,
  correction: ObservationCorrectionInput,
  correctedAt = new Date().toISOString()
): EpisodeWithCorrections {
  if (episode.status === 'closed') return episode as EpisodeWithCorrections

  const current = episode.observations.find((observation) => observation.id === observationId)
  if (!current) return episode as EpisodeWithCorrections

  const auditRecord: ObservationCorrectionRecord = {
    id: crypto.randomUUID(),
    correctedAt,
    reason: correction.reason?.trim() || undefined,
    previousCollectedAt: current.collectedAt,
    previousInput: current.input,
    previousResult: current.result
  }

  const observations: EpisodeObservation[] = episode.observations
    .map((observation) => {
      const input = observation.id === observationId ? correction.input : observation.input
      const collectedAt = observation.id === observationId ? correction.collectedAt : observation.collectedAt
      return {
        ...observation,
        collectedAt,
        input,
        result: evaluateClinicalState(input)
      }
    })
    .sort((a, b) => new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime())

  const firstConfirmed = observations.find((observation) => observation.result.diagnosis !== 'UNCLASSIFIED')
  const diagnosis = firstConfirmed?.result.diagnosis ?? 'UNCLASSIFIED'
  const typed = episode as EpisodeWithCorrections
  const previousAudit = typed.observationCorrections ?? {}
  const observationCorrections = {
    ...previousAudit,
    [observationId]: [...(previousAudit[observationId] ?? []), auditRecord]
  }

  const {
    resolutionConfirmation: _resolutionConfirmation,
    transitionPlan: _transitionPlan,
    observationCorrections: _oldCorrections,
    ...rest
  } = typed

  const rebuilt: EpisodeWithCorrections = {
    ...rest,
    startedAt: observations[0]?.collectedAt ?? episode.startedAt,
    status: 'active',
    diagnosis,
    observations,
    observationCorrections
  }

  if (!observations.length) return rebuilt
  const latest = observations[observations.length - 1]
  return { ...rebuilt, status: evaluateEpisode(rebuilt, latest.collectedAt).status }
}
